/**
 * Krok 1: Údaje o společnosti
 * Název firmy, IČO, kontaktní osoba, pro personalizaci reportu.
 */
import React, { useState } from 'react';
import { posunNaChybu } from '../utils/chyby';
import { TypPodnikaniVyber, segmentProTyp } from './TypPodnikani';
import { VyberOboru } from './VyberOboru';
import { rozpoznejObor } from '../api/oboryApi';

const ICO_REGEX = /^\d{8}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[\d\s\-()]{6,20}$/;

// Typy, pro které číselník oborů existuje. "Jiné zaměření" mezi nimi není,
// tam se obor jen popíše vlastními slovy.
const TYPY_S_OBOREM = ['eshop', 'obchod', 'vyroba', 'sluzby'];

// Hardcoded segment benchmarks (mirror of backend segments.py) for instant access without API call
const SEGMENT_BENCHMARKS = {
  supplements:     { benchmark_ebitda_margin: 22, benchmark_rrs: 43, benchmark_roas: 5.0, benchmark_inv_turnover: 8.0, benchmark_gross_margin: 55, benchmark_ltv_cac_ratio: 4.5, benchmark_repeat_purchase_rate: 45, benchmark_marketing_pct_revenue: 15 },
  beauty:          { benchmark_ebitda_margin: 22, benchmark_rrs: 36, benchmark_roas: 5.0, benchmark_inv_turnover: 7.0, benchmark_gross_margin: 55, benchmark_ltv_cac_ratio: 4.0, benchmark_repeat_purchase_rate: 40, benchmark_marketing_pct_revenue: 18 },
  pets:            { benchmark_ebitda_margin: 20, benchmark_rrs: 47, benchmark_roas: 4.5, benchmark_inv_turnover: 7.0, benchmark_gross_margin: 48, benchmark_ltv_cac_ratio: 5.0, benchmark_repeat_purchase_rate: 50, benchmark_marketing_pct_revenue: 12 },
  health_wellness: { benchmark_ebitda_margin: 20, benchmark_rrs: 39, benchmark_roas: 4.5, benchmark_inv_turnover: 7.0, benchmark_gross_margin: 50, benchmark_ltv_cac_ratio: 4.0, benchmark_repeat_purchase_rate: 42, benchmark_marketing_pct_revenue: 14 },
  food_beverages:  { benchmark_ebitda_margin: 18, benchmark_rrs: 47, benchmark_roas: 4.0, benchmark_inv_turnover: 10.0, benchmark_gross_margin: 45, benchmark_ltv_cac_ratio: 3.5, benchmark_repeat_purchase_rate: 48, benchmark_marketing_pct_revenue: 10 },
  fashion_own:     { benchmark_ebitda_margin: 15, benchmark_rrs: 30, benchmark_roas: 4.0, benchmark_inv_turnover: 5.0, benchmark_gross_margin: 55, benchmark_ltv_cac_ratio: 3.5, benchmark_repeat_purchase_rate: 32, benchmark_marketing_pct_revenue: 20 },
  sports_fitness:  { benchmark_ebitda_margin: 16, benchmark_rrs: 32, benchmark_roas: 4.0, benchmark_inv_turnover: 6.0, benchmark_gross_margin: 45, benchmark_ltv_cac_ratio: 3.0, benchmark_repeat_purchase_rate: 30, benchmark_marketing_pct_revenue: 15 },
  kids_own:        { benchmark_ebitda_margin: 16, benchmark_rrs: 32, benchmark_roas: 4.0, benchmark_inv_turnover: 6.0, benchmark_gross_margin: 50, benchmark_ltv_cac_ratio: 3.0, benchmark_repeat_purchase_rate: 35, benchmark_marketing_pct_revenue: 14 },
  hobby_lifestyle: { benchmark_ebitda_margin: 15, benchmark_rrs: 32, benchmark_roas: 3.5, benchmark_inv_turnover: 5.0, benchmark_gross_margin: 45, benchmark_ltv_cac_ratio: 2.5, benchmark_repeat_purchase_rate: 28, benchmark_marketing_pct_revenue: 12 },
  home_interior:   { benchmark_ebitda_margin: 14, benchmark_rrs: 25, benchmark_roas: 3.5, benchmark_inv_turnover: 4.0, benchmark_gross_margin: 42, benchmark_ltv_cac_ratio: 2.0, benchmark_repeat_purchase_rate: 20, benchmark_marketing_pct_revenue: 10 },
  garden_outdoor:  { benchmark_ebitda_margin: 14, benchmark_rrs: 25, benchmark_roas: 3.5, benchmark_inv_turnover: 4.0, benchmark_gross_margin: 40, benchmark_ltv_cac_ratio: 2.0, benchmark_repeat_purchase_rate: 22, benchmark_marketing_pct_revenue: 10 },
  toys:            { benchmark_ebitda_margin: 12, benchmark_rrs: 26, benchmark_roas: 3.5, benchmark_inv_turnover: 5.0, benchmark_gross_margin: 40, benchmark_ltv_cac_ratio: 2.0, benchmark_repeat_purchase_rate: 25, benchmark_marketing_pct_revenue: 12 },
  electronics:     { benchmark_ebitda_margin: 10, benchmark_rrs: 20, benchmark_roas: 3.0, benchmark_inv_turnover: 8.0, benchmark_gross_margin: 25, benchmark_ltv_cac_ratio: 1.8, benchmark_repeat_purchase_rate: 15, benchmark_marketing_pct_revenue: 8 },
  fashion_resale:  { benchmark_ebitda_margin: 10, benchmark_rrs: 23, benchmark_roas: 3.0, benchmark_inv_turnover: 5.0, benchmark_gross_margin: 35, benchmark_ltv_cac_ratio: 2.0, benchmark_repeat_purchase_rate: 22, benchmark_marketing_pct_revenue: 15 },
  dropshipping:    { benchmark_ebitda_margin: 8,  benchmark_rrs: 10, benchmark_roas: 3.0, benchmark_inv_turnover: 12.0, benchmark_gross_margin: 25, benchmark_ltv_cac_ratio: 1.5, benchmark_repeat_purchase_rate: 10, benchmark_marketing_pct_revenue: 25 },
};

export function Step0_CompanyInfo({ wizardState, setWizardState, onNext, onBack }) {
  const [form, setForm] = useState({ ...wizardState.company });
  const [oborCesta, setOborCesta] = useState(wizardState.obor_cesta || []);
  const [oborVlastni, setOborVlastni] = useState(wizardState.obor_vlastni || '');
  const [typPodnikani, setTypPodnikani] = useState(wizardState.typ_podnikani || '');
  const [errors, setErrors] = useState({});
  const [urcuje, setUrcuje] = useState(false);

  const jeEshop = typPodnikani === 'eshop';
  const maCiselnik = TYPY_S_OBOREM.includes(typPodnikani);


  const zmenTyp = (klic) => {
    setTypPodnikani(klic);
    // Každý typ má vlastní strom, takže cesta z předchozího typu by v něm
    // ukazovala jinam nebo nikam.
    setOborCesta([]);
    setOborVlastni('');
    setErrors(prev => ({ ...prev, typ_podnikani: null, obor: null }));
    // Zapisuje se hned, ne až po Pokračovat, aby lišta kroků rovnou ukázala,
    // že se průchod zkrátil. U výroby a služeb odpadají dva kroky.
    setWizardState(prev => ({ ...prev, typ_podnikani: klic }));
  };

  const zmenObor = (cesta, vlastni, novyTyp) => {
    // Z popisu může vyjít jiný typ, než klient zvolil: kdo napsal, že
    // prodává přes vlastní e-shop, patří k e-shopu, i když klikl na
    // Výrobu. Popis zná firmu líp než jeden klik, tak typ přepíšeme.
    if (novyTyp && novyTyp !== typPodnikani) {
      setTypPodnikani(novyTyp);
      setWizardState(prev => ({ ...prev, typ_podnikani: novyTyp }));
      setErrors(prev => ({ ...prev, typ_podnikani: null }));
    }
    setOborCesta(cesta);
    setOborVlastni(vlastni);
    if (errors.obor) setErrors(prev => ({ ...prev, obor: null }));
  };

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!(form.company_name || '').trim())    e.company_name = 'Povinné pole';
    if (!(form.ico || '').trim())             e.ico = 'Povinné pole';
    else if (!ICO_REGEX.test(form.ico.trim())) e.ico = 'IČO musí mít přesně 8 číslic';
    if (!typPodnikani) e.typ_podnikani = 'Vyberte, čím se firma zabývá';
    // Bez zaměření se hledají konkurenti podle typu podnikání, což u výroby
    // znamená srovnávat pekárnu se strojírnou. Stačí první úroveň nebo
    // vlastní popis, upřesnění je dobrovolné.
    else if (maCiselnik && !oborCesta.length && !oborVlastni.trim())
      e.obor = 'Vyberte zaměření, nebo ho popište vlastními slovy';
    // Kontakt je volitelný (předvyplněn z účtu); když je e-mail vyplněn, ověř formát
    if ((form.contact_email || '').trim() && !EMAIL_REGEX.test(form.contact_email.trim()))
      e.contact_email = 'Neplatný formát e-mailu';
    if ((form.contact_phone || '').trim() && !PHONE_REGEX.test(form.contact_phone.trim()))
      e.contact_phone = 'Neplatný formát telefonu';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = async () => {
    if (!validate()) { posunNaChybu(); return; }

    // E-shop potřebuje segment z číselníku, výpočet jiný nepřijme. Kdo obor
    // jen popsal a nepotvrdil tlačítkem Určit obor z popisu, dřív prošel dál
    // a výpočet pak spadl na neplatném segmentu. Popis se proto zařadí tady.
    let cesta = oborCesta, typ = typPodnikani;
    if (jeEshop && !cesta.length && oborVlastni.trim()) {
      setUrcuje(true);
      try {
        const v = await rozpoznejObor(oborVlastni.trim());
        if (v.rozpoznano && v.cesta && v.cesta.length) {
          cesta = v.cesta;
          typ = v.typ || typ;
          setOborCesta(cesta);
          if (typ !== typPodnikani) setTypPodnikani(typ);
        }
      } catch (e) {
        // Chybu řeší kontrola níže stejně jako nerozpoznaný popis.
      } finally {
        setUrcuje(false);
      }
      if (typ === 'eshop' && !cesta.length) {
        setErrors(prev => ({ ...prev, obor: 'Z popisu se zaměření určit nepodařilo. Vyberte ho prosím ze seznamu.' }));
        posunNaChybu();
        return;
      }
    }
    // Oborové benchmarky jsou vázané na e-commerce segmenty a první úroveň
    // číselníku je přesně ta sada klíčů. U ostatních typů benchmarky nemáme.
    const segment = typ === 'eshop' ? (cesta[0] || '') : segmentProTyp(typ);

    setWizardState(prev => ({
      ...prev,
      typ_podnikani: typ,
      segment_id: segment,
      segmentBenchmarks: SEGMENT_BENCHMARKS[segment] || null,
      obor_cesta: cesta,
      obor_vlastni: oborVlastni.trim(),
      company: {
        company_name:    (form.company_name || '').trim(),
        ico:             (form.ico || '').trim(),
        contact_name:    (form.contact_name || '').trim(),
        contact_email:   (form.contact_email || '').trim(),
        contact_phone:   (form.contact_phone || '').trim(),
        contact_position: (form.contact_position || '').trim(),
      },
      qualitative: {
        ...prev.qualitative,
        segment_id: segment,
      },
    }));
    onNext();
  };

  return (
    <div className="step-container">
      <h2>Společnost a zaměření</h2>
      <p className="step-description">
        Začněte tím, čím se firma zabývá. Podle toho se přizpůsobí další kroky:
        u výroby ani služeb se neptáme na reklamu a návštěvnost, protože tam
        nedávají smysl. Kontaktní údaje jsou předvyplněné z vašeho účtu.
      </p>

      <div className="edit-form">
        <TypPodnikaniVyber
          hodnota={typPodnikani}
          onChange={zmenTyp}
          chyba={errors.typ_podnikani}
        />

        {/* Zaměření. Nahrazuje dřívější plochý výběr segmentu: první úroveň
            číselníku je táž sada oborů, jen se dá pokračovat hlouběji. */}
        {typPodnikani && (
          <VyberOboru
            typ={typPodnikani}
            cesta={oborCesta}
            vlastni={oborVlastni}
            onChange={zmenObor}
            chyba={errors.obor}
          />
        )}

        {/* Firma */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--star-white)', marginBottom: '0.6rem' }}>
            Společnost
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Název společnosti *</label>
              <input
                type="text"
                value={form.company_name || ''}
                onChange={e => update('company_name', e.target.value)}
                placeholder="např. Novák a syn s.r.o."
              />
              {errors.company_name && <small className="chyba-pole">{errors.company_name}</small>}
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>IČO *</label>
              <input
                type="text"
                value={form.ico || ''}
                onChange={e => update('ico', e.target.value)}
                placeholder="12345678"
                maxLength={8}
              />
              {errors.ico && <small className="chyba-pole">{errors.ico}</small>}
            </div>
          </div>
        </div>

        {/* Kontaktní osoba (volitelné) */}
        <div>
          <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--star-white)', marginBottom: '0.6rem' }}>
            Kontaktní osoba <span style={{ fontWeight: 400, color: 'var(--text-dim)' }}>(volitelné, předvyplněno z účtu)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Jméno a příjmení</label>
              <input type="text" value={form.contact_name || ''} onChange={e => update('contact_name', e.target.value)} placeholder="Jan Novák" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Pozice ve firmě</label>
              <input type="text" value={form.contact_position || ''} onChange={e => update('contact_position', e.target.value)} placeholder="např. CEO, majitel" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>E-mail</label>
              <input type="email" value={form.contact_email || ''} onChange={e => update('contact_email', e.target.value)} placeholder="jan.novak@firma.cz" />
              {errors.contact_email && <small className="chyba-pole">{errors.contact_email}</small>}
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Telefon</label>
              <input type="tel" value={form.contact_phone || ''} onChange={e => update('contact_phone', e.target.value)} placeholder="+420 123 456 789" />
              {errors.contact_phone && <small className="chyba-pole">{errors.contact_phone}</small>}
            </div>
          </div>
        </div>
      </div>

      <div className="button-group">
        {onBack && <button onClick={onBack} className="btn-secondary">Zpět</button>}
        <button onClick={handleNext} className="btn-primary" disabled={urcuje}>{urcuje ? 'Určuji obor…' : 'Pokračovat'}</button>
      </div>
    </div>
  );
}
